import { useEffect, useState } from "react";
import { prefetchConferenceHub } from "@/lib/conferencePrefetch";

import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, University, MapPin, Calendar, Users, Heart, Book, Globe2 } from "lucide-react";
import StatsSection from "@/components/StatsSection";
import ofuesiLogo from "@/assets/ofuesi-logo.png";
import AnimatedUSMap from "@/components/AnimatedUSMap";
import EnhancedInteractiveMap from "@/components/EnhancedInteractiveMap";
import { ImageCarousel } from "@/components/ImageCarousel";
import { supabase } from "../../../src/integrations/supabase/client";
import conferenceWorship from "@/assets/Ofuesi_Conference_pic_1.jpg";
import conferencePrayer from "@/assets/Ofuesi_Conference_pic_2.jpg";
import conferenceKids from "@/assets/Ofuesi_Conference_pic_3.jpg";
import { useImagePreloader } from "@/hooks/useImagePreloader";
import PageLoader from "@/components/PageLoader";
import { useIsMobile } from "@/hooks/use-mobile";
import { MinistryRibbon } from "@/components/MinistryRibbon";
import { MagazineRibbon } from "@/components/MagazineRibbon";
import ConferenceFlashCardsModal from "@/components/ConferenceFlashCardsModal";
import GeneralBodyMeetingRibbon from "@/components/GeneralBodyMeetingRibbon";

const Index = () => {
  const [confEvents, setConfEvents] = useState<
    Array<{
      id: string;
      region_id: string;
      conference_name: string;
      conference_date: string | null;
      location: string | null;
      facilitators: Array<{ name: string; email: string }>;
    }>
  >([]);
  const [latestRegionalConferences, setLatestRegionalConferences] = useState<typeof confEvents>([]);
  const [regions, setRegions] = useState<Record<string, string>>({});
  const [presidentData, setPresidentData] = useState<{
    full_name: string;
    profile_image_url: string | null;
    notes: string | null;
  } | null>(null);

  // Detect mobile screen size
  const isMobile = useIsMobile();
  useEffect(() => {
    const load = async () => {
      try {
        const [eventsResult, regionsResult, presidentResult] = await Promise.all([
          supabase.from("conferences").select("*").order("conference_date", { ascending: false }),
          supabase.from("regions").select("*").order("name"),
          // members_public is a read-only database view not present in generated table types.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (supabase as any)
            .from("members_public")
            .select("full_name, profile_image_url")
            .eq("role", "president")
            .is("end_year", null)
            .maybeSingle(),
        ]);

        if (eventsResult.error) throw eventsResult.error;
        if (regionsResult.error) throw regionsResult.error;
        if (presidentResult.error) throw presidentResult.error;

        const allConferences = eventsResult.data || [];
        setConfEvents(allConferences);

        // Get latest conference from each region
        const regionMap = new Map<string, (typeof allConferences)[0]>();
        allConferences.forEach((conf) => {
          const existing = regionMap.get(conf.region_id);
          if (
            !existing ||
            (conf.conference_date && (!existing.conference_date || conf.conference_date > existing.conference_date))
          ) {
            regionMap.set(conf.region_id, conf);
          }
        });
        setLatestRegionalConferences(Array.from(regionMap.values()));

        const map: Record<string, string> = {};
        (regionsResult.data || []).forEach((r) => (map[r.id] = r.name));
        setRegions(map);
        setPresidentData(presidentResult.data);
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    load();
  }, []);
  const formatDate = (s?: string | null) => {
    if (!s) return "TBA";
    const d = new Date(s);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  const ministryHighlights = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Regional Fellowships",
      description: "Connect with students across 6 regions nationwide",
      link: "/regions",
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Prayer Ministry",
      description: "Join our interactive prayer map and support ministry",
      link: "/prayer",
    },
    {
      icon: <Book className="w-8 h-8" />,
      title: "Bible Studies",
      description: "Weekly studies at universities across United States of America",
      link: "/regions#regional-conferences",
    },
    {
      icon: <Globe2 className="w-8 h-8" />,
      title: "Women's Ministry",
      description: "Equipping women to grow in relationship with Christ",
      link: "/womens-ministry",
    },
  ];
  const highlightBgPalette = [
    "from-emerald-50 to-cyan-50",
    "from-amber-50 to-orange-50",
    "from-blue-50 to-indigo-50",
    "from-purple-50 to-pink-50",
  ];
  const highlightIconPalette = [
    "from-emerald-500 to-cyan-500",
    "from-amber-500 to-orange-500",
    "from-blue-500 to-indigo-500",
    "from-purple-500 to-pink-500",
  ];
  // Desktop images - high resolution
  const desktopHeroImages = [
    { url: conferenceWorship, alt: "OFUESI conference worship service" },
    { url: conferencePrayer, alt: "Prayer session at conference" },
    { url: conferenceKids, alt: "Ofuesi family" },
  ];

  // Mobile images - optimized for smaller screens
  // TODO: Replace with actual mobile-optimized images
  const mobileHeroImages = [
    { url: conferenceWorship, alt: "OFUESI conference worship service" },
    { url: conferencePrayer, alt: "Prayer session at conference" },
    { url: conferenceKids, alt: "Ofuesi family" },
  ];

  // Select appropriate image set based on screen size
  const heroImages = isMobile ? mobileHeroImages : desktopHeroImages;

  // Extract URLs for preloading
  const heroImageUrls = heroImages.map((img) => img.url);

  // Preload images with IndexedDB caching
  const { imagesLoaded, progress } = useImagePreloader({
    imageUrls: heroImageUrls,
    enabled: true,
  });

  // Define region sort order for conferences
  const REGION_ORDER = ["Northwest", "Mid West", "Eastern", "Southwest", "Southern", "SouthEast"];

  return (
    <div className="min-h-screen bg-[#ecf5fe]">
      <MagazineRibbon />
      <GeneralBodyMeetingRibbon />
      <MinistryRibbon />

      {/* Hero Carousel with About Overlay */}
      <section className="relative min-h-[285px] sm:min-h-[640px] md:min-h-[640px] lg:min-h-[700px] xl:min-h-[750px]">
        {/* Hero Carousel */}
        <div className="w-full overflow-hidden h-[285px] sm:h-[640px] md:h-[640px] lg:h-[700px] xl:h-[750px]">
          {!imagesLoaded ? (
            <div className="h-full w-full bg-[#ecf5fe] flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-lg font-medium text-primary">Loading....</p>
              </div>
            </div>
          ) : (
            <ImageCarousel images={heroImages} autoPlay className="h-full w-full" />
          )}
        </div>

        {/* About OFUESI Overlay - Hidden on mobile, shown on larger screens */}
        <div className="hidden sm:block absolute top-2 left-1/2 -translate-x-1/2 z-10 w-full max-w-3xl px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="p-2 bg-gradient-to-br from-black/60 via-black/50 to-black/60 backdrop-blur-md rounded-lg border border-white/20"
          >
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl lg:text-2xl font-bold text-white">We are OFUESI</h2>
              <p className="text-base lg:text-lg leading-relaxed text-white">
                Overseas Friends of Union of Evangelical Students of India – USA (OFUESI), is a Christian organization
                that supports the ongoing ministry of UESI through prayer and financial giving.
              </p>
            </div>
          </motion.div>
        </div>
      </section>





      {/* Mobile-only text section */}
      <section className="sm:hidden py-2">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-4"
          >
            <h2 className="text-3xl font-bold text-foreground">We are OFUESI</h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              Overseas Friends of Union of Evangelical Students of India – USA (OFUESI), is a Christian organization
              that supports the ongoing ministry of UESI through prayer and financial giving.
            </p>
          </motion.div>
        </div>
      </section>
      {/* President's Message Section */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Card className="">
              <CardHeader>
                <CardTitle className="text-center">President's Message</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {presidentData?.profile_image_url && (
                  <div className="flex justify-center mb-4">
                    <img
                      src={presidentData.profile_image_url}
                      alt="President"
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  </div>
                )}
                <div className="text-lg italic text-justify text-muted-foreground prose prose-sm max-w-none">
                  {presidentData?.notes ? (
                    <div dangerouslySetInnerHTML={{ __html: presidentData.notes }} />
                  ) : (
                    "Welcome to the official website of OFUESI! As we celebrated our Silver Jubilee in 2024, I invite you to explore our website, engage with our community, and connect with our regional groups."
                  )}
                </div>
                <div className="text-center">
                  <p className="font-semibold">{presidentData?.full_name || "Israel Nandamudi"}</p>
                  <p className="text-sm text-muted-foreground">President, OFUESI</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
      {/* Interactive US Map Hero Section */}
      <section className="relative">
        <div className="container mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <EnhancedInteractiveMap
              showStatsHeader={true}
              title="Connecting Students Across United States of America"
              description="Explore our active regions and discover fellowship opportunities near you"
            />
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <StatsSection />

      {/* Regional Conferences Section */}

      <section className="py-20 relative">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="text-center mb-12"
            initial={{
              opacity: 0,
              y: 30,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.6,
            }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-foreground">
              Nations came into our cities(Universities)
            </h2>
            <p className="text-xl text-muted-foreground">Let's Make Disciples</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(() => {
              const latestConferenceId =
                latestRegionalConferences.length > 0
                  ? latestRegionalConferences
                      .reduce((latest, current) => {
                        if (!latest.conference_date) return current;
                        if (!current.conference_date) return latest;
                        return new Date(current.conference_date) > new Date(latest.conference_date)
                          ? current
                          : latest;
                      }, latestRegionalConferences[0]).id
                  : null;
              return latestRegionalConferences
                .sort((a, b) => {
                  const regionA = regions[a.region_id] || "";
                  const regionB = regions[b.region_id] || "";
                  const indexA = REGION_ORDER.findIndex((r) => regionA.includes(r));
                  const indexB = REGION_ORDER.findIndex((r) => regionB.includes(r));
                  return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
                })
                .slice(0, 6)
                .map((ev, index) => {
                  const isLatest = ev.id === latestConferenceId;
                  return (
                    <Card
                      key={ev.id}
                      className={`divine-card h-full hover:shadow-lg transition-all duration-300 relative ${
                        isLatest ? "ring-2 ring-primary shadow-xl" : ""
                      }`}
                    >
                      {isLatest && (
                        <Button
                          size="sm"
                          className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 rounded-full px-3 h-auto text-xs shadow-md pointer-events-none"
                        >
                          Latest
                        </Button>
                      )}
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                          <span className="text-foreground font-bold">{regions[ev.region_id] || "Region"}</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="space-y-2 text-muted-foreground text-sm">
                          <div className="font-semibold text-foreground text-base">{ev.conference_name}</div>
                          {ev.location && (
                            <div className="flex items-center space-x-2">
                              <MapPin className="w-4 h-4 text-ministry-emerald" />
                              <span>{ev.location}</span>
                            </div>
                          )}
                          {ev.facilitators && ev.facilitators.length > 0 && (
                            <div className="flex items-start space-x-2">
                              <Users className="w-4 h-4 text-ministry-purple mt-0.5 flex-shrink-0" />
                              <div>
                                <span className="font-medium">Speakers: </span>
                                <span>{ev.facilitators.map((f) => f.name).join(", ")}</span>
                              </div>
                            </div>
                          )}
                        </div>
                        <motion.div
                          whileHover={{
                            scale: 1.05,
                          }}
                          whileTap={{
                            scale: 0.95,
                          }}
                        >
                          <Button
                            asChild
                            className="w-full text-white hover:bg-white hover:text-primary hover:border-primary hover:border-2 text-sm"
                          >
                            <Link to={`/conference`}>More Info</Link>
                          </Button>
                        </motion.div>
                      </CardContent>
                    </Card>
                  );
                });
            })()}
          </div>

          <motion.div
            className="text-center mt-12"
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            transition={{
              duration: 0.6,
              delay: 0.8,
            }}
          >
            <motion.div
              whileHover={{
                scale: 1.05,
              }}
              whileTap={{
                scale: 0.95,
              }}
            >
              <Button
                asChild
                size="lg"
                className="border-white text-white hover:bg-white hover:text-primary hover:border-primary hover:border-2"
              >
                <Link to="/conference" onMouseEnter={prefetchConferenceHub} onFocus={prefetchConferenceHub} onTouchStart={prefetchConferenceHub}>View All Conferences</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Ministry Highlights Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="text-center mb-12"
            initial={{
              opacity: 0,
              y: 30,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.6,
            }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-foreground">Join Our Ministry</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Discover the many ways you can participate in our mission to reach university students
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ministryHighlights.map((highlight, index) => (
              <Card
                className={`divine-card hover:shadow-lg transition-all duration-300 text-center h-full bg-gradient-to-br ${highlightBgPalette[index % highlightBgPalette.length]} transition-all duration-300 hover:brightness-105`}
              >
                <CardContent className="pt-8 pb-6 h-full flex flex-col">
                  <motion.div
                    className={`bg-gradient-to-br ${highlightIconPalette[index % highlightIconPalette.length]} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-white shadow-divine`}
                    whileHover={{
                      scale: 1.1,
                      rotate: 10,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                    }}
                  >
                    {highlight.icon}
                  </motion.div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">{highlight.title}</h3>
                  <p className="text-muted-foreground mb-4 flex-grow">{highlight.description}</p>
                  <motion.div
                    whileHover={{
                      scale: 1.05,
                    }}
                    whileTap={{
                      scale: 0.95,
                    }}
                  >
                    <Button
                      asChild
                      variant="outline"
                      className="bg-primary text-white border-2 border-primary hover:bg-white hover:text-primary transition-colors duration-300 cursor-pointer"
                    >
                      <Link to={highlight.link}>Learn More</Link>
                    </Button>
                  </motion.div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-8 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-ministry-cyan via-primary to-ministry-cyan"></div>
        <div className="absolute inset-0 faith-pattern opacity-20"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-white space-y-6"
            initial={{
              opacity: 0,
              y: 30,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.8,
            }}
          >
            <motion.h2
              className="text-3xl lg:text-4xl font-bold"
              initial={{
                opacity: 0,
                scale: 0.9,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              transition={{
                duration: 0.6,
                delay: 0.2,
              }}
            >
              Ready to Connect with Fellow Students?
            </motion.h2>
            <motion.p
              className="text-xl text-blue-100 leading-relaxed"
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              transition={{
                duration: 0.6,
                delay: 0.4,
              }}
            >
              Whether you're new to the United States or looking to deepen your faith journey, find your spiritual
              community with OFUESI.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{
                opacity: 0,
                y: 20,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.6,
                delay: 0.6,
              }}
            >
              <motion.div
                whileHover={{
                  scale: 1.05,
                }}
                whileTap={{
                  scale: 0.95,
                }}
              >
                <Button
                  asChild
                  size="lg"
                  className="bg-primary text-white border-2 border-primary hover:bg-white hover:text-primary transition-colors duration-300 cursor-pointer"
                >
                  <Link to="/new-students">Get Connected</Link>
                </Button>
              </motion.div>
              <motion.div
                whileHover={{
                  scale: 1.05,
                }}
                whileTap={{
                  scale: 0.95,
                }}
              >
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="bg-primary text-white border-2 border-primary hover:bg-white hover:text-primary transition-colors duration-300 cursor-pointer"
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <ConferenceFlashCardsModal />
    </div>
  );
};
export default Index;
